import { vsb } from 'common/flags';
import { getCorrelationId } from 'communicator/store/selectors';
import {
  exists,
  getErmEnabledCookieValue,
  getSsiEnabledCookieValue
} from 'communicator/utils/cookies';
import { RiskDecision } from 'types/api/intent';
import { AuthenticationReason } from 'types/emvco';
import {
  CheckoutOrchestrator,
  GAMPEvent,
  IdLookupStatus,
  RememberMeType,
  UserTypeIdentification,
  UserTypeShort,
  VisaSdkFlow
} from 'types/gamp';
import { ComplianceSettings } from 'types/visa-sdk/checkout';
import { InitData } from 'types/visa-sdk/init';

/**
  TODO: chopping block for deprecation
  auth_type
  campaign_flow
  order_type
  correlation_id
  flow
*/
/* START GENAI@GHCOPILOT */
let consumerPresent: boolean | undefined = undefined;
/* END GENAI@GHCOPILOT */
let detectedUserType: UserTypeShort;
let initData: InitData;
let srcInitiatorId: string | undefined;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let srcSystemName: string;
let gaClientId: string;

const srcInitiatorIdMap: {
  [key: string]: string;
} = {
  // VCO-14529: Mastercard Payment Gateway Services Limited apikey
  '6CB7SWU7FEU2MNQM6AIM21-4t0oWwhGCh1f8hfeN6DYg3FPLc': 'MPGS',
  // MA apikey
  JK0TVYHU43R1D9M2IU9G21BHE7Wxi584CeZQnZCffv4_o0ezc: 'MASTERCARD'
};

/* START GENAI@GHCOPILOT */
// _ga client ID is formated as 123456789.1234567890
const generateGAMPClientId = (): string => {
  const random = Math.floor(Math.random() * 10000000000);
  return `${Date.now()}.${random}`;
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
// return singleton client ID for GA Measurement Protocol
const getGAMPClientId = (): string => {
  if (!gaClientId) {
    gaClientId = generateGAMPClientId();
  }

  return gaClientId;
};
/* END GENAI@GHCOPILOT */

const getUserTypeId = (): UserTypeIdentification => {
  if (detectedUserType === 'Recognized') {
    return 'Recognized user';
  } else if (
    consumerPresent ||
    getErmEnabledCookieValue() ||
    getSsiEnabledCookieValue() ||
    exists('ssiDeclined')
  ) {
    return 'Unrecognized Returning user';
  } else if (consumerPresent === false) {
    return 'New user';
  }

  return 'Undefined';
};

/* START: GENAI:GHCOPILOT */
/*
 * GA funnels expect country only - normalize to return 2-letter country code
 * dpaLocale is defined in EMVCo spec as ISO language country pair. [ISO 639-1 Code] [ISO 3166-1 alpha-2 country code]
 * Examples: en-US, en_US, fil-PH
 * Not supported: ar-INT, gr-INT (INT is not a valid country code; should be UNDEFINED in this case)
 */
const getCountry = (dpaLocale: string | undefined): string => {
  if (dpaLocale) {
    if (/[a-z]{2,3}[-_]{1}[a-z]{2}/i.test(dpaLocale)) {
      // en-us or en-US, en_US, fil-PH
      return dpaLocale.substring(dpaLocale.length - 2, dpaLocale.length).toUpperCase();
    } else if (/[a-z]{2}/i.test(dpaLocale)) {
      // us or US
      return dpaLocale.toUpperCase();
    }
  }

  return 'UNDEFINED';
};
/* END: GENAI:GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const getRememberMeType = (
  complianceSettings: ComplianceSettings | undefined
): RememberMeType => {
  if (
    complianceSettings &&
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    complianceSettings.complianceResources &&
    complianceSettings.complianceResources.length > 0
  ) {
    const isRememberMeSelected = complianceSettings.complianceResources.some(resource => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return resource && resource.complianceType && resource.complianceType === 'REMEMBER_ME';
    });

    if (isRememberMeSelected) {
      return 'SELECTED' as RememberMeType;
    }
  }

  return 'UNDEFINED' as RememberMeType;
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
// MP event names MUST be snake_case
const getGAMPName = (event: string): string => {
  return event.replace(/ /g, '_').toLowerCase();
};
/* END GENAI@GHCOPILOT */

export const pushGampData = (data: GAMPEvent) => {
  try {
    /* START GENAI@GHCOPILOT */
    const measurementId = window.VISA_GLOBALS.gampId || 'G-H3DCNE46F4'; // dev-default
    const apiSecret = window.VISA_GLOBALS.gampApiSecret || 'UmqHjB1OT1-KbjUMJg1UQQ'; // dev-default
    // GA Measurement Protocol direct POST
    const payload = {
      client_id: getGAMPClientId(),
      timestamp_micros: Date.now() * 1000,
      events: [
        {
          name: getGAMPName(data.event || data.event_action || data.event_label || 'undefined'),
          params: {
            ...data
          }
        }
      ]
    };
    /* END GENAI@GHCOPILOT */

    /* START GENAI@GHCOPILOT */
    fetch(
      `https://www.google-analytics.com/mp/collect?measurement_id=${measurementId}&api_secret=${apiSecret}`,
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    );

    /* END GENAI@GHCOPILOT */
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`GA MP push failure for: ${JSON.stringify(e, null, 2)}`);
  }
};

export const getBaseApiEvent = (): GAMPEvent => {
  const apiKey = initData.srcInitiatorId || initData.customData?.apikey;
  srcInitiatorId = initData.srcInitiatorId;
  srcSystemName = apiKey && srcInitiatorIdMap[apiKey] ? srcInitiatorIdMap[apiKey] : 'VISA';

  const baseApiEvent = {
    api_key: apiKey,
    Checkout_orchestrator: initData.dpaTransactionOptions?.customInputData
      ?.checkoutOrchestrator as CheckoutOrchestrator,
    correlation_id: getCorrelationId(),
    dpa_id: initData.dpaData?.srcDpaId || initData.srciDpaId, // or clause is for SRC SDK TESTER INIT DATA
    dpa_name: initData.dpaData?.dpaPresentationName,
    event: undefined,
    event_action: undefined,
    event_label: undefined,
    flow: vsb || location.search.includes('vsb') ? 'VSB' : ('MOC' as VisaSdkFlow),
    merchant_country_code: getCountry(initData.dpaTransactionOptions?.dpaLocale),
    src_initiator_id: srcInitiatorId
  };

  // cannot memo as value changes through various flows
  //baseApiEvent.user_type = detectedUserType;
  return baseApiEvent;
};

/* START GENAI@GHCOPILOT */
export const pushInit = (data: InitData, d: number) => {
  initData = data;
  const x = 'Init called';
  pushGampData({
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: x,
    event_label: x,
    /* START GENAI@GHCOPILOT */
    user_type_identification: 'Undefined'
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushIsRecognizedRequest = () => {
  const x = 'IsRecognized called';
  const y = 'IsRecognized request received';

  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: y,
    event_label: y,
    /* START GENAI@GHCOPILOT */
    user_type_identification: 'Undefined'
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushIsRecognizedResponse = (userType: UserTypeShort, d: number) => {
  detectedUserType = userType;
  const x = 'IsRecognized called';
  const y = 'IsRecognized response';

  pushGampData({
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: y,
    /* START: GENAI:GHCOPILOT */
    event_label: `${y}: ${detectedUserType === 'Recognized' ? 'true' : 'false'}`,
    /* END: GENAI:GHCOPILOT */
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushIdentityLookupRequest = () => {
  const x = 'Identity lookup';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: `${x} request`,
    event_label: `${x} request`,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushIdentityLookupResponse = (lookupStatus: IdLookupStatus, d: number) => {
  const x = 'Identity lookup';
  consumerPresent = lookupStatus === 'PRESENT';
  pushGampData({
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: `${x} response`,
    event_label: `${x} response: ${lookupStatus === 'PRESENT' ? 'true' : 'false'}`,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushInitiateIdentityValidationRequest = () => {
  const x = 'Initiate Identity validation';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: `${x} request`,
    event_label: `${x} request`,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

// START GENAI@GHCOPILOT
export const pushInitiateIdentityValidationResponse = (d: number) => {
  const x = 'Initiate Identity validation';
  pushGampData({
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: `${x} response`,
    event_label: `${x} response`,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
// END GENAI@GHCOPILOT

/* START GENAI@GHCOPILOT */
export const pushCompleteIdentityValidationRequest = () => {
  const x = 'Complete Identity validation';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: `${x} request`,
    event_label: `${x} request`,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushCompleteIdentityValidationResponse = (
  isSuccessfullyVerified: boolean,
  d: number
) => {
  const x = 'Complete Identity validation';
  pushGampData({
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: `${x} response`,
    event_label: `${x} response: ${
      isSuccessfullyVerified === true ? 'successfully verified' : 'not successfully verified'
    }`,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushGetSrcProfileRequest = () => {
  const x = 'Get SRC profile request';
  const event = {
    ...getBaseApiEvent(),
    event: x,
    event_action: x,
    event_label: x,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  };

  pushGampData(event);
};
/* END GENAI@GHCOPILOT */

// START GENAI@GHCOPILOT
export const pushGetSrcProfileResponse = (d: number) => {
  const x = 'Get SRC profile response';
  const event = {
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: `${x} response`,
    event_label: `${x} response`,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  };

  pushGampData(event);
};
// END GENAI@GHCOPILOT

export const pushCheckoutCardSelected = () => {
  const x = 'Card selected';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: x,
    event_label: `${x}-API: VISA`
  });
};

/* START GENAI@GHCOPILOT */
export const pushCheckoutRequest = (
  isCardEnrollment: boolean,
  rememberMe: RememberMeType = 'UNDEFINED',
  authContextAuthReasons: AuthenticationReason | 'UNDEFINED' = 'UNDEFINED'
) => {
  const x = 'Checkout request';
  pushGampData({
    ...getBaseApiEvent(),
    AuthContext_AuthReasons: authContextAuthReasons,
    /* START GENAI@GHCOPILOT */
    CardEnrollment: isCardEnrollment ? 'Yes' : 'No',
    /* END GENAI@GHCOPILOT */
    event: x,
    event_action: x,
    event_label: x,
    remember_me: rememberMe,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId()
    /* END GENAI@GHCOPILOT */
  });
};
/* END GENAI@GHCOPILOT */

export const pushCheckoutResponse = (
  isCheckoutComplete: boolean,
  isCardEnrollment: boolean,
  rememberMe: RememberMeType = 'UNDEFINED',
  authContextAuthReasons: AuthenticationReason | 'UNDEFINED' = 'UNDEFINED',
  isVisaRiskStepUp = false,
  d: number
) => {
  /* START GENAI@GHCOPILOT */
  const x = 'Checkout response';
  pushGampData({
    ...getBaseApiEvent(),
    AuthContext_AuthReasons: authContextAuthReasons,
    /* START GENAI@GHCOPILOT */
    CardEnrollment: isCardEnrollment ? 'Yes' : 'No',
    /* END GENAI@GHCOPILOT */
    duration: d,
    event: x,
    event_action: x,
    event_label: `${x}: ${isCheckoutComplete === true ? 'Complete' : 'Failed'}`,
    remember_me: rememberMe,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId(),
    /* END GENAI@GHCOPILOT */
    /* START: GENAI:GHCOPILOT */
    VISA_risk_step_up: isVisaRiskStepUp ? 'Yes' : 'No'
    /* END GENAI@GHCOPILOT */
  });
  /* END GENAI@GHCOPILOT */
};

export const pushUnbind = () => {
  const x = 'Unbind application';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: x,
    event_label: x
  });
};

export const pushAuthenticateGenerateFlowInitiated = () => {
  const x = 'Authenticate Generate Started';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: x,
    event_label: x
  });
};

export const pushAuthenticateValidateFlowInitiated = () => {
  const x = 'Authenticate Validate Started';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: x,
    event_label: x
  });
};

export const pushAuthenticateGenerateFlowFinished = (d: number) => {
  const x = 'Authenticate Generate Finished';
  pushGampData({
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: x,
    event_label: x
  });
};

export const pushAuthenticateValidateFlowFinished = (d: number) => {
  const x = 'Authenticate Validate Finished';
  pushGampData({
    ...getBaseApiEvent(),
    duration: d,
    event: x,
    event_action: x,
    event_label: x
  });
};

/* START GENAI@GHCOPILOT */
export const pushVisaRiskStepUp = (
  isEnroll = false,
  rememberMe: RememberMeType = 'UNDEFINED',
  authContextAuthReasons: AuthenticationReason | 'UNDEFINED' = 'UNDEFINED',
  riskDecision: RiskDecision | undefined,
  d: number
) => {
  const x = 'Visa Risk Step-up';
  pushGampData({
    ...getBaseApiEvent(),
    AuthContext_AuthReasons: authContextAuthReasons,
    duration: d,
    event: x,
    event_action: x,
    event_label: x,
    risk_decision: riskDecision,
    /* START GENAI@GHCOPILOT */
    user_type_identification: getUserTypeId(),
    /* END GENAI@GHCOPILOT */
    /* START: GENAI:GHCOPILOT */
    VISA_risk_step_up: 'Yes'
    /* END GENAI@GHCOPILOT */
  });

  pushCheckoutResponse(false, isEnroll, rememberMe, authContextAuthReasons, true, d);
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushErrors = (errorType: string, error: unknown) => {
  const x = 'Error';

  if (typeof error !== 'string') {
    error = JSON.stringify(error);
  }

  pushGampData({
    ...getBaseApiEvent(),
    error: typeof error === 'string' ? error : 'Unknown error',
    event: x,
    event_action: `${x}: ${errorType}`,
    event_label: `${x}: ${errorType}`
  });
};
/* END GENAI@GHCOPILOT */
